import React, { useState } from 'react';
import { useApp } from '../context/AppContext';
import { Templates } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Button } from './ui/button';
import { Popover, PopoverTrigger, PopoverContent } from './ui/popover';
import { Mail, Save, Info, Sparkles, Plus, Trash2, PenLine } from 'lucide-react';
import { toast } from '../hooks/use-toast';

// All merge fields supported by both Guest and Prospect email replacement maps
const MERGE_FIELDS = [
  { tag: '{{firstName}}', desc: 'Guest/Prospect first name' },
  { tag: '{{showName}}', desc: 'Active show name' },
  { tag: '{{episode}}', desc: 'Guest episode title' },
  { tag: '{{recordingTime}}', desc: 'Recording date + time (host TZ)' },
  { tag: '{{episodeDate}}', desc: 'Publish date' },
  { tag: '{{releaseLink}}', desc: 'Public release form URL' },
  { tag: '{{appleLink}}', desc: 'Apple Podcasts URL' },
  { tag: '{{websiteLink}}', desc: 'Episode page on the show website' },
  { tag: '{{spotifyLink}}', desc: 'Spotify URL' },
  { tag: '{{youtubeLink}}', desc: 'YouTube URL' },
  { tag: '{{riversideLink}}', desc: 'Riverside studio URL' },
  { tag: '{{bookingLink}}', desc: 'Public booking URL (prospects)' },
  { tag: '{{supportLink}}', desc: 'Support page (combined)' },
  { tag: '{{listenersSupportLink}}', desc: 'Listeners support page' },
  { tag: '{{guestsSupportLink}}', desc: 'Guests & PR support page' },
  { tag: '{{referrer}}', desc: 'Prospect → Source detail (prospects)' },
  { tag: '{{topic}}', desc: 'Prospect → Source detail (prospects)' },
  { tag: '{{company}}', desc: 'Prospect/Guest → Company (prospects)' }
];

const SEEDED_ID = /^([tp]\d+|a\d+)$/;

function TemplateEditor({ template, onSaved }) {
  const [name, setName] = useState(template.name || '');
  const [subject, setSubject] = useState(template.subject || '');
  const [body, setBody] = useState(template.body || '');
  const [saving, setSaving] = useState(false);
  const isCustom = !SEEDED_ID.test(template.id);
  const dirty = subject !== (template.subject || '') || body !== (template.body || '') || name !== (template.name || '');

  const save = async () => {
    setSaving(true);
    try {
      await Templates.update(template.id, { ...template, name, subject, body });
      toast({ title: 'Template saved', description: name });
      onSaved?.();
    } catch (e) {
      toast({ title: 'Save failed', description: e?.response?.data?.detail || String(e) });
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!window.confirm(`Delete "${name}"? This can't be undone.`)) return;
    await Templates.remove(template.id);
    toast({ title: 'Letter deleted', description: name });
    onSaved?.();
  };

  return (
    <Card className="border-stone-200">
      <CardHeader className="pb-2 flex flex-row items-center justify-between space-y-0">
        <CardTitle className="text-[14.5px] flex items-center gap-2 flex-1 min-w-0">
          {isCustom ? <PenLine className="w-4 h-4 text-stone-500 shrink-0" /> : <Mail className="w-4 h-4 text-stone-500 shrink-0" />}
          {isCustom ? (
            <Input data-testid={`tpl-name-${template.id}`} value={name} onChange={(e) => setName(e.target.value)} placeholder="Name your letter" className="h-7 text-[13.5px] font-semibold" />
          ) : (
            <span className="truncate">{template.name}</span>
          )}
        </CardTitle>
        <div className="flex items-center gap-1.5">
          <Popover>
            <PopoverTrigger asChild>
              <button type="button" data-testid={`merge-info-${template.id}`} className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded border border-stone-200 hover:bg-stone-50 text-[10.5px] text-stone-600">
                <Info className="w-3 h-3" /> Merge fields
              </button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-[320px] p-0">
              <div className="px-3 py-2 border-b border-stone-200 text-[12px] font-medium text-stone-800 flex items-center gap-1">
                <Sparkles className="w-3 h-3" /> Click a tag to copy
              </div>
              <div className="max-h-[260px] overflow-y-auto divide-y divide-stone-100">
                {MERGE_FIELDS.map(f => (
                  <button
                    key={f.tag}
                    onClick={() => { navigator.clipboard?.writeText(f.tag); toast({ title: 'Copied', description: f.tag }); }}
                    className="w-full text-left px-3 py-1.5 hover:bg-stone-50 text-[11.5px]"
                  >
                    <code className="bg-stone-100 px-1.5 py-0.5 rounded text-stone-800">{f.tag}</code>
                    <span className="ml-2 text-stone-500">{f.desc}</span>
                  </button>
                ))}
              </div>
            </PopoverContent>
          </Popover>
          {dirty && <span className="text-[10.5px] text-amber-600 font-medium">unsaved</span>}
        </div>
      </CardHeader>
      <CardContent className="space-y-2">
        <div>
          <div className="text-[11px] uppercase text-stone-500 mb-1">Subject</div>
          <Input data-testid={`tpl-subject-${template.id}`} value={subject} onChange={(e) => setSubject(e.target.value)} className="text-[13px]" />
        </div>
        <div>
          <div className="text-[11px] uppercase text-stone-500 mb-1">Body</div>
          <Textarea data-testid={`tpl-body-${template.id}`} value={body} onChange={(e) => setBody(e.target.value)} rows={Math.min(14, Math.max(6, body.split('\n').length))} className="text-[12.5px] font-mono whitespace-pre" />
        </div>
        <div className="flex justify-between items-center">
          {isCustom ? (
            <button type="button" data-testid={`tpl-delete-${template.id}`} onClick={remove} className="inline-flex items-center gap-1 text-[11px] text-stone-400 hover:text-red-600">
              <Trash2 className="w-3 h-3" /> Delete letter
            </button>
          ) : <span />}
          <Button data-testid={`tpl-save-${template.id}`} onClick={save} disabled={!dirty || saving} size="sm">
            <Save className="w-3.5 h-3.5 mr-1.5" /> {saving ? 'Saving…' : dirty ? 'Save changes' : 'Saved'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

export default function TemplatesEditor() {
  const { templates, refreshTemplates } = useApp();
  const [creating, setCreating] = useState('');
  const groups = [
    { key: 'guest', title: 'Guest letters', desc: 'Release requests, reminders, confirmations and asset delivery for booked guests.' },
    { key: 'prospect', title: 'Prospect outreach — Podcast Guests', desc: 'Reach-out letters inviting people to come on the show.' },
    { key: 'app_user', title: 'Prospect outreach — App Users', desc: 'Letters pitching this app to podcasters who could use it for their own guest pipeline.' },
  ];

  const createLetter = async (category) => {
    setCreating(category);
    try {
      await Templates.create({ name: 'My new letter', subject: '', body: '', category });
      await refreshTemplates();
      toast({ title: 'Blank letter created', description: 'Name it and write your letter below, then Save changes.' });
    } catch (e) {
      toast({ title: 'Could not create letter', description: e?.response?.data?.detail || String(e) });
    } finally {
      setCreating('');
    }
  };

  return (
    <div className="h-full overflow-auto">
      <div className="p-5">
        <div className="text-[11px] uppercase text-stone-500 mb-3">
          {templates.length} email templates · click any field to edit, then Save changes. Use <code className="bg-stone-100 px-1 py-0.5 rounded">{'{{tag}}'}</code> merge fields — open the popover on each card for the live list.
        </div>
        {groups.map(g => {
          const list = templates.filter(t => (t.category || 'guest') === g.key);
          return (
            <div key={g.key} className="mb-6" data-testid={`template-group-${g.key}`}>
              <div className="mb-2 flex items-end justify-between gap-3">
                <div>
                  <div className="text-[13.5px] font-semibold text-stone-800">{g.title}</div>
                  <div className="text-[11.5px] text-stone-500">{g.desc}</div>
                </div>
                <Button size="sm" variant="outline" data-testid={`new-letter-btn-${g.key}`} onClick={() => createLetter(g.key)} disabled={creating === g.key} className="shrink-0">
                  <Plus className="w-3.5 h-3.5 mr-1" /> {creating === g.key ? 'Creating…' : 'Write a new letter'}
                </Button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {list.map(t => (
                  <TemplateEditor key={t.id} template={t} onSaved={refreshTemplates} />
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
